import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, Users, Phone, X, Check, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

const ReservationSection = () => {
  const { language } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    guests: '2',
    notes: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate submission
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setIsModalOpen(false);
      setFormData({
        name: '',
        phone: '',
        email: '',
        date: '',
        time: '',
        guests: '2',
        notes: ''
      });
    }, 3000);
  };

  const timeSlots = [
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'
  ];

  return (
    <>
      <section 
        id="reservation" 
        className="relative overflow-hidden bg-gradient-to-b from-hanse-navy to-hanse-dark py-16 md:py-20"
        data-testid="reservation-section"
      >
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            {/* Sparkle Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 mb-4 bg-gradient-to-r from-amber-500/20 to-amber-600/20 backdrop-blur-sm px-5 py-2 rounded-full border border-amber-400/30"
            >
              <Sparkles className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 font-semibold text-sm uppercase tracking-wider">
                {language === 'de' ? 'Tisch Reservieren' : 'Reserve a Table'}
              </span>
              <Sparkles className="w-5 h-5 text-amber-400" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
            >
              {language === 'de' ? 'Online Tischreservierung' : 'Online Table Reservation'}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/60 text-base md:text-lg max-w-2xl mx-auto"
            >
              {language === 'de' 
                ? 'Reservieren Sie Ihren Tisch bequem online oder rufen Sie uns direkt an.'
                : 'Reserve your table conveniently online or call us directly.'
              }
            </motion.p>
          </div>

          {/* Two Options Grid */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            
            {/* Online Reservation Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10 hover:border-amber-400/30 transition-all duration-300"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Calendar className="w-7 h-7 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-white">
                    {language === 'de' ? 'Online Reservieren' : 'Reserve Online'}
                  </h3>
                  <p className="text-white/50 text-sm">
                    {language === 'de' ? 'Schnell & einfach' : 'Quick & easy'}
                  </p>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-white/70 text-sm">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  {language === 'de' ? 'Sofortige Bestätigung' : 'Instant confirmation'}
                </li>
                <li className="flex items-center gap-3 text-white/70 text-sm">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  {language === 'de' ? '24/7 verfügbar' : 'Available 24/7'}
                </li>
                <li className="flex items-center gap-3 text-white/70 text-sm">
                  <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  {language === 'de' ? 'Sonderwünsche möglich' : 'Special requests welcome'}
                </li>
              </ul>

              <Button
                onClick={() => setIsModalOpen(true)}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-4 rounded-xl text-base"
                data-testid="reserve-online-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                {language === 'de' ? 'Jetzt Reservieren' : 'Reserve Now'}
              </Button>
            </motion.div>

            {/* Call Us Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-hanse-red/20 to-hanse-red/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-hanse-red/20 hover:border-hanse-red/40 transition-all duration-300"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-hanse-red/20 rounded-full flex items-center justify-center">
                  <Phone className="w-7 h-7 text-hanse-red" />
                </div>
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-white">
                    {language === 'de' ? 'Rufen Sie uns an' : 'Call Us'}
                  </h3>
                  <p className="text-white/50 text-sm">
                    {language === 'de' ? 'Persönliche Beratung' : 'Personal assistance'}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-white/70 text-sm mb-3">
                  {language === 'de' 
                    ? 'Für Gruppenreservierungen oder besondere Anlässe rufen Sie uns gerne direkt an.'
                    : 'For group reservations or special occasions, feel free to call us directly.'
                  }
                </p>
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-white/50 text-xs mb-1">
                    {language === 'de' ? 'Telefon' : 'Phone'}
                  </p>
                  <p className="text-amber-400 text-2xl font-bold">+49 421 48535145</p>
                  <p className="text-white/40 text-xs mt-2">
                    {language === 'de' ? 'Mo-So 11:00 - 22:00' : 'Mon-Sun 11:00 - 22:00'}
                  </p>
                </div>
              </div>

              <Button
                onClick={() => window.location.href = 'tel:+4942148535145'}
                className="w-full bg-hanse-red hover:bg-hanse-red/90 text-white font-bold py-4 rounded-xl text-base"
                data-testid="call-reservation-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                {language === 'de' ? 'Jetzt Anrufen' : 'Call Now'}
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reservation Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-b from-hanse-dark to-hanse-navy rounded-2xl p-6 md:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-white">
                    {language === 'de' ? 'Tisch Reservieren' : 'Reserve a Table'}
                  </h3>
                  <p className="text-white/50 text-sm">
                    {language === 'de' ? 'Füllen Sie das Formular aus' : 'Fill out the form'}
                  </p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-10 h-10 text-green-400" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    {language === 'de' ? 'Reservierung Gesendet!' : 'Reservation Sent!'}
                  </h4>
                  <p className="text-white/60 text-sm">
                    {language === 'de' 
                      ? 'Wir werden Sie in Kürze bestätigen.'
                      : 'We will confirm your reservation shortly.'
                    }
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-white/70 text-sm mb-2">
                      {language === 'de' ? 'Name *' : 'Name *'}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-amber-400/50 focus:outline-none transition-colors"
                      placeholder={language === 'de' ? 'Ihr Name' : 'Your name'}
                    />
                  </div>

                  {/* Phone & Email Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        {language === 'de' ? 'Telefon *' : 'Phone *'}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-amber-400/50 focus:outline-none transition-colors"
                        placeholder="+49..."
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-amber-400/50 focus:outline-none transition-colors"
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>

                  {/* Date & Time Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        {language === 'de' ? 'Datum *' : 'Date *'}
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-amber-400/50 focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {language === 'de' ? 'Uhrzeit *' : 'Time *'}
                      </label>
                      <select
                        name="time"
                        value={formData.time}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-amber-400/50 focus:outline-none transition-colors"
                      >
                        <option value="" className="bg-hanse-dark">
                          {language === 'de' ? 'Wählen...' : 'Select...'}
                        </option>
                        {timeSlots.map(time => (
                          <option key={time} value={time} className="bg-hanse-dark">{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Guests */}
                  <div>
                    <label className="block text-white/70 text-sm mb-2">
                      <Users className="w-4 h-4 inline mr-1" />
                      {language === 'de' ? 'Anzahl Gäste *' : 'Number of Guests *'}
                    </label>
                    <select
                      name="guests"
                      value={formData.guests}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-amber-400/50 focus:outline-none transition-colors"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                        <option key={num} value={num} className="bg-hanse-dark">
                          {num} {language === 'de' ? (num === 1 ? 'Person' : 'Personen') : (num === 1 ? 'Guest' : 'Guests')}
                        </option>
                      ))}
                      <option value="10+" className="bg-hanse-dark">10+ ({language === 'de' ? 'Bitte anrufen' : 'Please call'})</option>
                    </select>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-white/70 text-sm mb-2">
                      {language === 'de' ? 'Besondere Wünsche' : 'Special Requests'}
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-amber-400/50 focus:outline-none transition-colors resize-none"
                      placeholder={language === 'de' ? 'Allergien, Kinderstuhl, etc.' : 'Allergies, high chair, etc.'}
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-4 rounded-xl text-base mt-6"
                  >
                    {language === 'de' ? 'Reservierung Absenden' : 'Submit Reservation'}
                  </Button>

                  <p className="text-white/40 text-xs text-center">
                    {language === 'de' 
                      ? '* Pflichtfelder. Wir bestätigen Ihre Reservierung per Telefon oder E-Mail.'
                      : '* Required fields. We will confirm your reservation by phone or email.'
                    }
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ReservationSection;
